import React, { useState } from "react";
import { FieldArray, FormikErrors, FormikProvider, useFormik } from "formik";
import { AddPhotoAlternate, Close, Delete } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as Yup from "yup";

import { mainCategory } from "../../../customer/data/mainCategory";
import { menLevelTwo } from "../../../customer/data/levelTwo/menLevelTwo";
import { womenLevelTwo } from "../../../customer/data/levelTwo/womenLevelTwo";
import { electronicsLevelTwo } from "../../../customer/data/levelTwo/electronicsLevelTwo";
import { furnitureLevelTwo } from "../../../customer/data/levelTwo/funitureLevelTwo";
import { beautyLevelTwo } from "../../../customer/data/levelTwo/beautyLevelTwo";
import { menLevelThree } from "../../../customer/data/levelThree/menLevelThree";
import { womenLevelThree } from "../../../customer/data/levelThree/womenLevelThree";
import { electronicsLevelThree } from "../../../customer/data/levelThree/electronicLevelThree";
import { furnitureLevelThree } from "../../../customer/data/levelThree/funitureLevelThree";
import { beautyLevelThree } from "../../../customer/data/levelThree/beautyLevelThree";
import { useAppDispatch } from "../../../State/Store";
import { createProduct } from "../../../State/seller/sellerProductSlice";
import { useNavigate } from "react-router-dom";
import { ProductDetailVariant } from "../../components/ProductDetailVariant";
import {
  CreateProductRequest,
  ProductDetail,
} from "../../../types/productType";
import { uploadImageToCloudinary } from "../../../State/seller/sellerUploadToCloudinary";

const categoryTwo: { [key: string]: any[] } = {
  men: menLevelTwo,
  women: womenLevelTwo,
  electronics: electronicsLevelTwo,
  home_furniture: furnitureLevelTwo,
  beauty: beautyLevelTwo,
};
const categoryThree: { [key: string]: any[] } = {
  men: menLevelThree,
  women: womenLevelThree,
  electronics: electronicsLevelThree,
  home_furniture: furnitureLevelThree,
  beauty: beautyLevelThree,
};
const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  description: Yup.string()
    .required("Description is required and must be max 350 words")
    .test("maxWords", "Description must be at most 350 words", (value) => {
      if (!value) return true; // Let required() handle empty cases
      const wordCount = value.trim().split(/\s+/).length;

      return wordCount <= 350; // This should return false if limit is exceeded
    }),

  mrpPrice: Yup.number()
    .required("MRP Price is required")
    .moreThan(0, "Price must be greater than 0"),
  sellingPrice: Yup.number()
    .required("Selling Price is required")
    .moreThan(0, "Price must be greater than 0"),
  quantity: Yup.number()
    .required("Quantity is required")
    .integer("Quantity must be a whole number") // Ensures no decimal values
    .min(1, "At least 1 item must be in stock"),
  images: Yup.array()
    .test("isArray", "Images must be an array", (value) => Array.isArray(value))
    .required("At least one image is required")
    .min(1, "At least one image is required"),
  category: Yup.string().required("Category is required"),
  details: Yup.array()
    .of(
      Yup.object({
        size: Yup.string().required("Size is required"),
        color: Yup.string().required("Color is required"),
        sellingPrice: Yup.number()
          .typeError("Must be a number")
          .min(0)
          .required("Required"),
        stock: Yup.number()
          .typeError("Must be a number")
          .min(0)
          .integer()
          .required("Required"),
      })
    )
    .min(1, "Add at least one detail")
    .test("uniq-size-color", "Duplicate size + color not allowed", (arr) => {
      if (!arr) return true;
      const seen = new Set<string>();
      for (const r of arr) {
        const key = `${r.size}__${r.color}`;
        if (!r.size || !r.color) continue;
        if (seen.has(key)) return false;
        seen.add(key);
      }
      return true;
    }),
});
const emptyRow = { size: "", color: "", sellingPrice: 0, stock: 0 };

export const AddProducts = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const formik = useFormik<CreateProductRequest>({
    initialValues: {
      title: "", // Product
      description: "", // Product description
      mrpPrice: 0, // Set as number
      sellingPrice: 0, // Set as number
      quantity: 0, // Set as number
      images: [], // Array to hold uploaded image URLs
      category: "", // Primary category
      category2: "", // Secondary category (optional)
      category3: "", // Tertiary category (optional)
      // discountPercentage: 0, // Initialize to a default value
      // createdAt: new Date(), // Add default value
      details: [emptyRow],
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    onSubmit: async (values, { resetForm }) => {
      
      await dispatch(createProduct(values));
      resetForm();
      navigate("/seller/products");
    },
  });
  const [uploadImage, setUploadImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // at top of component
  const [selectedIdx, setSelectedIdx] = React.useState<number>(0);

  // Filtering out category child match with parent category
  const childCategory = (category: any, categoryId: any) => {
    if (!Array.isArray(category)) {
      return []; // Return an empty array to prevent crashes
    }
    return category.filter(
      (child: any) => child.parentCategoryId == categoryId
    );
  };

  const handleRemoveImage = (index: number) => {
    // Remove the image at the specified index
    const updatedImages = [...formik.values.images];
    // remove the image from the images array
    updatedImages.splice(index, 1);
    // Update the images field in Formik
    formik.setFieldValue("images", updatedImages);
  };
  // Since the image upload is triggered directly from the onChange event of the file input, there's no need to use a useEffect in this case. The handleImageChange function takes care of managing the image upload and updating the form state.

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadImage(true);
      const url = await dispatch(uploadImageToCloudinary(file)).unwrap();
      formik.setFieldValue("images", [...formik.values.images, url]);
    } catch (err) {
      console.error(err);
    } finally {
      setUploadImage(false);
      e.target.value = "";
    }
  };
  const uploadFile = async (file: File) => {
    try {
      setUploadImage(true);
      const url = await dispatch(uploadImageToCloudinary(file)).unwrap();
      formik.setFieldValue("images", [...formik.values.images, url]);
    } catch (err) {
      console.error(err);
    } finally {
      setUploadImage(false);
    }
  };
  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    await uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  return (
    <FormikProvider value={formik}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit(e);
        }}
        className="space-y-4 p-4"
      >
        <Grid2 container spacing={2}>
          <Grid2 className="flex flex-wrap gap-5" size={{ xs: 12 }}>
            {/* Hidden File Input */}
            <input
              type="file"
              accept="image/*"
              id="fileInput"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />

            {/* Custom Button for File Input */}
            <label
              htmlFor="fileInput"
              className={`relative  ${
                isDragging
                  ? "border-dashed border-2 border-teal-500 bg-teal-50"
                  : ""
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <span className="w-24 h-24 cursor-pointer flex items-center justify-center border rounded-md">
                <AddPhotoAlternate className="text-gray-700" />
              </span>
              {/* Show Circular Progress if Uploading */}
              {uploadImage && (
                <div className="absolute left-0 right-0 top-0 bottom-0 w-24 h-24 flex items-center justify-center">
                  <CircularProgress />
                </div>
              )}
            </label>

            <div className="flex flex-wrap gap-2">
              {formik.values.images.map((image: string, index: number) => (
                <div className="relative" key={index}>
                  {/* Render the uploaded image */}
                  <img
                    className="w-24 h-24 object-cover"
                    key={index}
                    src={image}
                    alt={`ProductImage ${index + 1}`}
                  />

                  {/* Render the delete button */}
                  <IconButton
                    onClick={() => handleRemoveImage(index)}
                    className=""
                    size="small"
                    color="error"
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      outline: "none",
                    }}
                  >
                    <Close />
                  </IconButton>
                </div>
              ))}
            </div>
          </Grid2>
          {formik.touched.images && formik.errors.images && (
            <p
              style={{
                color: "#d32f2f",
                fontSize: "0.875rem",
                marginTop: "4px",
              }}
            >
              *{formik.errors.images}
            </p>
          )}
          <Grid2 size={{ xs: 12, sm: 12 }}>
            <TextField
              fullWidth
              id="title"
              name="title"
              label="Title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12 }}>
            <TextField
              fullWidth
              id="quantity"
              name="quantity"
              label="Quantity"
              value={formik.values.quantity}
              onChange={formik.handleChange}
              error={formik.touched.quantity && Boolean(formik.errors.quantity)}
              helperText={formik.touched.quantity && formik.errors.quantity}
              required
            />

            <h1 className="pt-3">Product Details:</h1>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12 }}>
            <FieldArray
              name="details"
              render={(helpers) => (
                <>
                  {formik.values.details.map((row, idx) => (
                    <ProductDetailVariant
                      isOnlyProduct={formik.values.details.length <= 1}
                      key={idx}
                      idx={idx}
                      values={row} // 👈 pass row directly
                      handleChange={formik.handleChange}
                      touched={formik.touched.details?.[idx]}
                      errors={
                        formik.errors.details?.[idx] as
                          | FormikErrors<ProductDetail>
                          | undefined
                      }
                      onRemove={() => helpers.remove(idx)}
                      disableRemove={formik.values.details.length === 1}
                      onActivate={() => setSelectedIdx(idx)}
                    />
                  ))}

                  <div className="w-full flex justify-between ">
                    <Grid2>
                      <Button
                        variant="outlined"
                        onClick={() => helpers.push(emptyRow)} // 👈 this now works fine
                      >
                        Add More Product Details
                      </Button>
                    </Grid2>
                    <Grid2>
                    </Grid2>
                  </div>
                </>
              )}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <TextField
              multiline
              rows={9}
              fullWidth
              id="description"
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              required
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              id="sellingPrice"
              name="sellingPrice"
              label="Selling Price"
              type="number"
              inputMode="decimal"
              value={formik.values.sellingPrice}
              onChange={formik.handleChange}
              error={
                formik.touched.sellingPrice &&
                Boolean(formik.errors.sellingPrice)
              }
              helperText={
                formik.touched.sellingPrice && formik.errors.sellingPrice
              }
              required
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              id="mrp_price"
              name="mrpPrice"
              label="MRP Price"
              type="number"
              inputMode="decimal"
              value={formik.values.mrpPrice}
              onChange={formik.handleChange}
              error={formik.touched.mrpPrice && Boolean(formik.errors.mrpPrice)}
              helperText={formik.touched.mrpPrice && formik.errors.mrpPrice}
              required
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4, lg: 4 }}>
            <FormControl
              fullWidth
              error={formik.touched.category && Boolean(formik.errors.category)}
              required
            >
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                id="category"
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                label="Category"
              >
                {mainCategory.map((item, index) => (
                  <MenuItem key={index} value={item?.categoryId}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.category && formik.errors.category && (
                <FormHelperText>{formik.errors.category}</FormHelperText>
              )}
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4, lg: 4 }}>
            <FormControl
              fullWidth
              error={formik.touched.category && Boolean(formik.errors.category)}
              required
            >
              <InputLabel id="category2-label">Second Category</InputLabel>
              <Select
                labelId="category2-label"
                id="category2"
                name="category2"
                value={formik.values.category2}
                onChange={formik.handleChange}
                label="Second Category"
              >
                {formik.values.category &&
                  categoryTwo[formik.values.category]?.map((item, index) => (
                    <MenuItem key={index} value={item.categoryId}>
                      {item.name}
                    </MenuItem>
                  ))}
              </Select>
              {formik.touched.category && formik.errors.category && (
                <FormHelperText>{formik.errors.category}</FormHelperText>
              )}
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4, lg: 4 }}>
            <FormControl
              fullWidth
              error={formik.touched.category && Boolean(formik.errors.category)}
              required
            >
              <InputLabel id="category3-label">Third Category</InputLabel>
              <Select
                labelId="category3-label"
                id="category3"
                name="category3"
                value={formik.values.category3}
                onChange={formik.handleChange}
                label="Third Category"
              >
                {formik.values.category &&
                  childCategory(
                    categoryThree[formik.values.category],
                    formik.values.category2
                  ).map((item: any, index) => (
                    <MenuItem key={index} value={item.categoryId}>
                      {item.name}
                    </MenuItem>
                  ))}
              </Select>
              {formik.touched.category && formik.errors.category && (
                <FormHelperText>{formik.errors.category}</FormHelperText>
              )}
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Button
              sx={{ p: "14px" }}
              color="primary"
              variant="contained"
              fullWidth
              type="submit"
              // onClick={() => console.log("Submit button clicked")}
              // disabled={sellerProduct.loading}
            >
              {false ? (
                <CircularProgress
                  size="small"
                  sx={{ width: "27px", height: "27px" }}
                />
              ) : (
                "Add Product"
              )}
            </Button>
          </Grid2>
        </Grid2>
      </form>
    </FormikProvider>
  );
};
